import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { RyanSubdeckGroupData, RyanTab } from '../types/ryan.types';
import { RYAN_GROUP_ICONS, getRyanIconComponent } from '../utils/ryanGroupIcons';

const COLOR_GROUPS = [
  {
    label: 'Verdes',
    colors: ['#0D3B0E', '#1B5E20', '#2E7D32', '#388E3C', '#4CAF50', '#33691E', '#558B2F', '#689F38', '#7CB342', '#9E9D24'],
  },
  {
    label: 'Teal & Ciano',
    colors: ['#004D40', '#00695C', '#00796B', '#00897B', '#006064', '#00838F', '#0097A7', '#00ACC1'],
  },
  {
    label: 'Azuis & Índigos',
    colors: ['#0D2B8F', '#1565C0', '#0277BD', '#01579B', '#283593', '#311B92', '#4527A0', '#512DA8'],
  },
  {
    label: 'Roxos & Vinhos',
    colors: ['#4A148C', '#6A1B9A', '#7B1FA2', '#880E4F', '#AD1457', '#C2185B'],
  },
  {
    label: 'Âmbar & Terrosos',
    colors: ['#E65100', '#F57C00', '#FF8F00', '#F9A825', '#827717', '#BF360C', '#4E342E', '#5D4037'],
  },
  {
    label: 'Neutros',
    colors: ['#263238', '#37474F', '#455A64', '#546E7A', '#607D8B', '#6B7280'],
  },
];

interface RyanCreateGroupModalProps {
  spaceId: string;
  tabs: RyanTab[];
  activeTabId: string;
  editingGroup?: RyanSubdeckGroupData | null;
  onClose: () => void;
  onSuccess: () => void;
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}

const RyanCreateGroupModal: React.FC<RyanCreateGroupModalProps> = ({
  spaceId, tabs, activeTabId, editingGroup, onClose, onSuccess, onToast,
}) => {
  const isEditing = !!editingGroup;

  const [name, setName] = useState(editingGroup?.name || '');
  const [description, setDescription] = useState(editingGroup?.description || '');
  const [iconName, setIconName] = useState(editingGroup?.iconName || 'Layers');
  const [color, setColor] = useState(editingGroup?.color || '#0D3B0E');
  const [tabId, setTabId] = useState<string>(editingGroup?.tabId || activeTabId || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [iconSearch, setIconSearch] = useState('');

  useEffect(() => {
    if (editingGroup) {
      setName(editingGroup.name);
      setDescription(editingGroup.description || '');
      setIconName(editingGroup.iconName);
      setColor(editingGroup.color);
      setTabId(editingGroup.tabId || '');
    }
  }, [editingGroup]);

  const filteredIcons = iconSearch.trim()
    ? RYAN_GROUP_ICONS.filter(ic =>
        ic.label.toLowerCase().includes(iconSearch.toLowerCase()) ||
        ic.name.toLowerCase().includes(iconSearch.toLowerCase())
      )
    : RYAN_GROUP_ICONS;

  const handleSave = async () => {
    if (!name.trim()) { setError('Nome obrigatório'); return; }
    setSaving(true);
    setError('');
    const token = localStorage.getItem('accessToken');
    try {
      const url = isEditing
        ? `/api/ryan/admin/spaces/${spaceId}/groups/${editingGroup!._id}`
        : `/api/ryan/admin/spaces/${spaceId}/groups`;
      const method = isEditing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: name.trim(),
          description,
          iconName,
          color,
          tabId: tabId || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json();
        setError(body.message || 'Erro ao salvar grupo');
        return;
      }
      onToast(isEditing ? 'Grupo atualizado!' : 'Grupo criado!', 'success');
      onSuccess();
      onClose();
    } catch {
      setError('Erro de conexão');
    } finally {
      setSaving(false);
    }
  };

  const SelectedIcon = getRyanIconComponent(iconName);

  return (
    <div className="ryan-modal-overlay" onClick={onClose}>
      <div className="ryan-modal ryan-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="ryan-modal-header">
          <h3 className="ryan-modal-title">{isEditing ? 'Editar Grupo' : 'Criar Grupo'}</h3>
          <button className="ryan-modal-close-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="ryan-modal-body">
          <div className="ryan-modal-field">
            <label className="ryan-modal-label">Nome *</label>
            <input
              className="ryan-modal-input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ex: Cardiologia"
              autoFocus
              maxLength={60}
            />
          </div>

          <div className="ryan-modal-field">
            <label className="ryan-modal-label">Descrição</label>
            <input
              className="ryan-modal-input"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Descrição breve (opcional)"
              maxLength={200}
            />
          </div>

          {tabs.length > 0 && (
            <div className="ryan-modal-field">
              <label className="ryan-modal-label">Aba</label>
              <select
                className="ryan-modal-select"
                value={tabId}
                onChange={e => setTabId(e.target.value)}
              >
                <option value="">Sem aba</option>
                {tabs.slice().sort((a, b) => a.order - b.order).map(tab => (
                  <option key={tab._id} value={tab._id}>{tab.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="ryan-modal-field">
            <label className="ryan-modal-label">Cor</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {COLOR_GROUPS.map(group => (
                <div key={group.label}>
                  <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary, #888)', fontFamily: 'Inter, sans-serif', marginBottom: 6 }}>
                    {group.label}
                  </div>
                  <div className="ryan-group-color-grid">
                    {group.colors.map(c => (
                      <button
                        key={c}
                        className={`ryan-group-color-dot${color === c ? ' selected' : ''}`}
                        style={{ backgroundColor: c }}
                        onClick={() => setColor(c)}
                        title={c}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="ryan-modal-field">
            <label className="ryan-modal-label">
              Ícone
              <span style={{ marginLeft: 8, fontWeight: 400, color: 'var(--text-secondary)' }}>
                — selecionado:
              </span>
              <span style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <SelectedIcon size={16} color={color} />
                <span style={{ fontSize: 12 }}>{iconName}</span>
              </span>
            </label>
            <input
              className="ryan-modal-input"
              placeholder="Buscar ícone…"
              value={iconSearch}
              onChange={e => setIconSearch(e.target.value)}
              style={{ marginBottom: 8 }}
            />
            <div className="ryan-group-icon-picker">
              {filteredIcons.map(ic => {
                const IconComp = ic.component;
                return (
                  <button
                    key={ic.name}
                    className={`ryan-group-icon-option${iconName === ic.name ? ' selected' : ''}`}
                    title={ic.label}
                    onClick={() => setIconName(ic.name)}
                  >
                    <IconComp size={18} />
                  </button>
                );
              })}
            </div>
          </div>

          {error && <div className="ryan-modal-error">{error}</div>}
        </div>

        <div className="ryan-modal-footer">
          <button className="ryan-modal-cancel-btn" onClick={onClose}>Cancelar</button>
          <button className="ryan-modal-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando…' : isEditing ? 'Salvar' : 'Criar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RyanCreateGroupModal;
